import React, { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { DsButton, DsIcon, DsInput, DsLabel } from "../../../design-system";
import usePolicyCompanyRoles from "../policy-wizard/usePolicyCompanyRoles";
import { getRoleLabel } from "../../../utils/roleSelectOptions";

function slugify(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 32) || `${Date.now()}`;
}

function findLevelPolicy(levelPolicies, scope, ids) {
  return (levelPolicies || []).find((p) => {
    if (!p || p.scope !== scope) return false;
    if (scope === "type") return p.typeId === ids.typeId;
    if (scope === "category")
      return p.typeId === ids.typeId && p.categoryId === ids.categoryId;
    if (scope === "subcategory")
      return (
        p.typeId === ids.typeId &&
        p.categoryId === ids.categoryId &&
        p.subcategoryId === ids.subcategoryId
      );
    return false;
  });
}

function ensureLevelPolicy(list, scope, ids) {
  const existing = findLevelPolicy(list, scope, ids);
  if (existing) return { list, policy: existing };
  const policy = {
    id: `lp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    scope,
    typeId: ids.typeId || null,
    categoryId: ids.categoryId || null,
    subcategoryId: ids.subcategoryId || null,
  };
  return { list: [...list, policy], policy };
}

function FormModal({ open, title, onClose, children, footer, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/50 p-4">
      <div
        className={`flex max-h-[90vh] w-full flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)] ${
          wide ? "max-w-4xl" : "max-w-lg"
        }`}
      >
        <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">{title}</h3>
          <DsButton type="button" variant="ghost" size="sm" onClick={onClose}>
            Close
          </DsButton>
        </div>
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
        {footer ? (
          <div className="flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-4 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Panel({ title, description, right, children }) {
  return (
    <div className="overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-4 py-3">
        <div>
          <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">{title}</h3>
          {description ? (
            <p className="mt-0.5 ds-caption text-[hsl(var(--ds-muted-fg))]">{description}</p>
          ) : null}
        </div>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function RoleMultiSelect({ options, value = [], onChange, disabled }) {
  const selected = Array.isArray(value) ? value : [];
  return (
    <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-[hsl(var(--ds-border))] p-2">
      {options.length === 0 ? (
        <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
          No roles found. Create roles under User Roles.
        </p>
      ) : (
        options.map((opt) => {
          const checked = selected.some(
            (v) => String(v).toLowerCase() === String(opt.value).toLowerCase()
          );
          return (
            <label key={opt.value} className="flex items-center gap-2 ds-body">
              <input
                type="checkbox"
                disabled={disabled}
                checked={checked}
                onChange={(e) => {
                  if (e.target.checked) {
                    onChange([...selected.filter((v) => String(v).toLowerCase() !== String(opt.value).toLowerCase()), opt.value]);
                  } else {
                    onChange(
                      selected.filter(
                        (v) => String(v).toLowerCase() !== String(opt.value).toLowerCase()
                      )
                    );
                  }
                }}
              />
              <span>
                {opt.label}
                {opt.isSystem ? (
                  <span className="ml-1 ds-caption text-[hsl(var(--ds-muted-fg))]">system</span>
                ) : null}
              </span>
            </label>
          );
        })
      )}
    </div>
  );
}

/**
 * Two-level catalog:
 * - Type = structure only
 * - Category = leaf selection + full ticket config (priorities, SLA, workflow, …)
 * Company roles from User Roles power escalation + assignment pickers.
 */
export default function HelpdeskCatalogPanel({
  config,
  safeSetConfig,
  setLevelPolicyConfig,
  canEdit,
  canEditLevelPolicies,
  saveFlash,
}) {
  const { policyRoleOptions } = usePolicyCompanyRoles();
  const types = config.masters.ticketTypes || [];
  const categories = config.masters.categories || [];
  const priorities = config.masters.priorities || [];
  const statuses = config.masters.statuses || [];

  const [typeForm, setTypeForm] = useState(null);
  const [categoryForm, setCategoryForm] = useState(null);
  const [baselinesOpen, setBaselinesOpen] = useState(false);
  const [categoryConfig, setCategoryConfig] = useState(null);
  const [configTab, setConfigTab] = useState("priorities");

  const patchLevelPolicy = (scope, ids, section, value) => {
    if (!canEditLevelPolicies) return;
    setLevelPolicyConfig((cfg) => {
      const ensured = ensureLevelPolicy(cfg.levelPolicies || [], scope, ids);
      const nextList = ensured.list.map((p) =>
        p.id === ensured.policy.id ? { ...p, [section]: value } : p
      );
      return { ...cfg, levelPolicies: nextList };
    });
  };

  const clearLevelSection = (scope, ids, section) => {
    if (!canEditLevelPolicies) return;
    setLevelPolicyConfig((cfg) => {
      const nextList = (cfg.levelPolicies || [])
        .map((p) => {
          const match =
            p.scope === scope &&
            p.typeId === (ids.typeId || null) &&
            (scope === "type" || p.categoryId === (ids.categoryId || null)) &&
            (scope !== "subcategory" ||
              p.subcategoryId === (ids.subcategoryId || null));
          if (!match) return p;
          const next = { ...p };
          delete next[section];
          return next;
        })
        .filter((p) =>
          ["sla", "workflow", "rca", "priorities", "assignment", "notifications", "advanced"].some(
            (key) => p[key] !== undefined
          )
        );
      return { ...cfg, levelPolicies: nextList };
    });
  };

  const seedPriorities = (overrideList) => {
    if (Array.isArray(overrideList) && overrideList.length) {
      return priorities.map((p) => {
        const hit = overrideList.find((x) => x.id === p.id) || {};
        return {
          id: p.id,
          name: p.name,
          enabled: hit.enabled !== undefined ? hit.enabled : p.enabled !== false,
          firstResponseMinutes:
            hit.firstResponseMinutes ?? p.firstResponseMinutes,
          resolutionMinutes: hit.resolutionMinutes ?? p.resolutionMinutes,
          autoUpgradeAfterMinutes:
            hit.autoUpgradeAfterMinutes ?? p.autoUpgradeAfterMinutes ?? null,
        };
      });
    }
    return priorities.map((p) => ({
      id: p.id,
      name: p.name,
      enabled: p.enabled !== false,
      firstResponseMinutes: p.firstResponseMinutes,
      resolutionMinutes: p.resolutionMinutes,
      autoUpgradeAfterMinutes: p.autoUpgradeAfterMinutes ?? null,
    }));
  };

  /* ——— Type CRUD ——— */
  const openNewType = () => setTypeForm({ mode: "create", name: "", enabled: true });
  const openEditType = (t) =>
    setTypeForm({ mode: "edit", id: t.id, name: t.name, enabled: t.enabled !== false });

  const saveTypeForm = () => {
    if (!canEdit || !typeForm?.name?.trim()) return;
    const name = typeForm.name.trim();
    if (typeForm.mode === "create") {
      const id = slugify(name);
      safeSetConfig((c) => ({
        ...c,
        masters: {
          ...c.masters,
          ticketTypes: [...c.masters.ticketTypes, { id, name, enabled: !!typeForm.enabled }],
        },
      }));
    } else {
      safeSetConfig((c) => ({
        ...c,
        masters: {
          ...c.masters,
          ticketTypes: c.masters.ticketTypes.map((x) =>
            x.id === typeForm.id
              ? { ...x, name, enabled: !!typeForm.enabled }
              : x
          ),
        },
      }));
    }
    setTypeForm(null);
    saveFlash();
  };

  const deleteType = (t) => {
    if (!canEdit) return;
    if (!window.confirm(`Delete type "${t.name}" and all its categories?`)) return;
    safeSetConfig((c) => ({
      ...c,
      masters: {
        ...c.masters,
        ticketTypes: c.masters.ticketTypes.filter((x) => x.id !== t.id),
        categories: c.masters.categories.filter((x) => x.typeId !== t.id),
      },
    }));
    saveFlash();
  };

  /* ——— Category CRUD + priorities ——— */
  const openNewCategory = () => {
    setCategoryForm({
      mode: "create",
      typeId: types[0]?.id || "",
      name: "",
      enabled: true,
    });
  };
  const openEditCategory = (c) =>
    setCategoryForm({
      mode: "edit",
      id: c.id,
      typeId: c.typeId,
      name: c.name,
      enabled: c.enabled !== false,
    });

  const saveCategoryForm = () => {
    if (!canEdit || !categoryForm?.name?.trim() || !categoryForm?.typeId) return;
    const name = categoryForm.name.trim();
    if (categoryForm.mode === "create") {
      const id = slugify(name);
      safeSetConfig((c) => ({
        ...c,
        masters: {
          ...c.masters,
          categories: [
            ...c.masters.categories,
            {
              id,
              typeId: categoryForm.typeId,
              name,
              enabled: !!categoryForm.enabled,
            },
          ],
        },
      }));
    } else {
      safeSetConfig((c) => ({
        ...c,
        masters: {
          ...c.masters,
          categories: c.masters.categories.map((x) =>
            x.id === categoryForm.id
              ? {
                  ...x,
                  name,
                  typeId: categoryForm.typeId,
                  enabled: !!categoryForm.enabled,
                }
              : x
          ),
        },
      }));
    }
    setCategoryForm(null);
    saveFlash();
  };

  const deleteCategory = (c) => {
    if (!canEdit) return;
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    safeSetConfig((cfg) => ({
      ...cfg,
      masters: {
        ...cfg.masters,
        categories: cfg.masters.categories.filter((x) => x.id !== c.id),
      },
    }));
    saveFlash();
  };

  const openCategoryConfigure = (c) => {
    const typeName = types.find((t) => t.id === c.typeId)?.name || c.typeId;
    setConfigTab("priorities");
    setCategoryConfig({
      scope: "category",
      ids: { typeId: c.typeId, categoryId: c.id },
      label: `${typeName} › ${c.name}`,
    });
  };

  const activeCategoryPolicy = categoryConfig
    ? findLevelPolicy(config.levelPolicies, "category", categoryConfig.ids)
    : null;

  const categoryPriorityRows = seedPriorities(activeCategoryPolicy?.priorities);

  const categoryEscalation =
    activeCategoryPolicy?.sla?.escalationMatrix ||
    config.sla?.escalationMatrix ||
    [];

  const updateCategorySla = (patch) => {
    patchLevelPolicy("category", categoryConfig.ids, "sla", {
      ...(activeCategoryPolicy?.sla || {}),
      ...patch,
    });
  };

  const fieldClass =
    "w-full rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm text-[hsl(var(--ds-fg))]";

  return (
    <div className="space-y-4">
      <div>
        <h1 className="ds-h1 text-[hsl(var(--ds-fg))]">Ticket catalog</h1>
        <p className="mt-1 ds-body text-[hsl(var(--ds-muted-fg))]">
          Two levels only: <strong>Ticket types</strong> and <strong>Categories</strong>.
          Categories are what users pick when raising a ticket. Use <strong>Configure</strong> on a
          category for priorities, SLA, workflow, and assignment. Company defaults live under{" "}
          <strong>Default priorities</strong>.
        </p>
      </div>

      {/* ——— Types grid ——— */}
      <Panel
        title="Ticket types"
        description="Top-level division. Add types, then map categories under them."
        right={
          <DsButton type="button" size="sm" disabled={!canEdit} onClick={openNewType}>
            <DsIcon icon={Plus} size="sm" /> Add type
          </DsButton>
        }
      >
        {types.length === 0 ? (
          <p className="ds-body text-[hsl(var(--ds-muted-fg))]">No ticket types yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="ds-table w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]">
                  <th className="px-2 py-2 font-semibold">Name</th>
                  <th className="px-2 py-2 font-semibold">Key</th>
                  <th className="px-2 py-2 font-semibold">Categories</th>
                  <th className="px-2 py-2 font-semibold">Status</th>
                  <th className="px-2 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {types.map((t) => {
                  const catCount = categories.filter((c) => c.typeId === t.id).length;
                  return (
                    <tr
                      key={t.id}
                      className="border-b border-[hsl(var(--ds-border))] last:border-0"
                    >
                      <td className="px-2 py-2.5 font-medium">{t.name}</td>
                      <td className="px-2 py-2.5 ds-caption text-[hsl(var(--ds-muted-fg))]">
                        {t.id}
                      </td>
                      <td className="px-2 py-2.5">{catCount}</td>
                      <td className="px-2 py-2.5">
                        {t.enabled !== false ? "Enabled" : "Disabled"}
                      </td>
                      <td className="px-2 py-2.5">
                        <div className="flex flex-wrap justify-end gap-1.5">
                          <DsButton
                            type="button"
                            size="sm"
                            variant="ghost"
                            disabled={!canEdit}
                            onClick={() => openEditType(t)}
                          >
                            Edit
                          </DsButton>
                          <DsButton
                            type="button"
                            size="sm"
                            variant="ghost"
                            disabled={!canEdit}
                            onClick={() => deleteType(t)}
                          >
                            <DsIcon icon={Trash2} size="sm" />
                          </DsButton>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

            {/* ——— Categories grid ——— */}
      <Panel
        title="Categories"
        description="Leaf ticket selection under a type. Configure holds priorities, SLA, workflow, RCA, assignment, and advanced rules."
        right={
          <div className="flex flex-wrap gap-2">
            <DsButton
              type="button"
              size="sm"
              variant="outline"
              disabled={!canEdit}
              onClick={() => setBaselinesOpen(true)}
            >
              Default priorities
            </DsButton>
            <DsButton
              type="button"
              size="sm"
              disabled={!canEdit || types.length === 0}
              onClick={openNewCategory}
            >
              <DsIcon icon={Plus} size="sm" /> Add category
            </DsButton>
          </div>
        }
      >
        {categories.length === 0 ? (
          <p className="ds-body text-[hsl(var(--ds-muted-fg))]">
            No categories yet. Add a type first, then Add category.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="ds-table w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]">
                  <th className="px-2 py-2 font-semibold">Name</th>
                  <th className="px-2 py-2 font-semibold">Key</th>
                  <th className="px-2 py-2 font-semibold">Mapped type</th>
                  <th className="px-2 py-2 font-semibold">Status</th>
                  <th className="px-2 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => {
                  const typeName =
                    types.find((t) => t.id === c.typeId)?.name || c.typeId || "—";
                  return (
                    <tr
                      key={c.id}
                      className="border-b border-[hsl(var(--ds-border))] last:border-0"
                    >
                      <td className="px-2 py-2.5 font-medium">{c.name}</td>
                      <td className="px-2 py-2.5 ds-caption text-[hsl(var(--ds-muted-fg))]">
                        {c.id}
                      </td>
                      <td className="px-2 py-2.5">{typeName}</td>
                      <td className="px-2 py-2.5">
                        {c.enabled !== false ? "Enabled" : "Disabled"}
                      </td>
                      <td className="px-2 py-2.5">
                        <div className="flex flex-wrap justify-end gap-1.5">
                          <DsButton
                            type="button"
                            size="sm"
                            disabled={!canEditLevelPolicies}
                            onClick={() => openCategoryConfigure(c)}
                          >
                            Configure
                          </DsButton>
                          <DsButton
                            type="button"
                            size="sm"
                            variant="ghost"
                            disabled={!canEdit}
                            onClick={() => openEditCategory(c)}
                          >
                            Edit
                          </DsButton>
                          <DsButton
                            type="button"
                            size="sm"
                            variant="ghost"
                            disabled={!canEdit}
                            onClick={() => deleteCategory(c)}
                          >
                            <DsIcon icon={Trash2} size="sm" />
                          </DsButton>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <FormModal
        open={baselinesOpen}
        title="Default priority baselines"
        onClose={() => setBaselinesOpen(false)}
        wide
        footer={
          <DsButton
            type="button"
            onClick={() => {
              setBaselinesOpen(false);
              saveFlash();
            }}
          >
            Done
          </DsButton>
        }
      >
        <p className="mb-3 ds-caption text-[hsl(var(--ds-muted-fg))]">
          Company fallback SLAs used when a category has not set its own priorities in Configure.
        </p>
        <div className="space-y-3">
          {priorities.map((p) => (
            <div
              key={p.id}
              className="grid grid-cols-1 gap-3 rounded-lg border border-[hsl(var(--ds-border))] p-3 md:grid-cols-4"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{p.name}</span>
                <label className="inline-flex items-center gap-1 ds-caption">
                  <input
                    type="checkbox"
                    checked={p.enabled !== false}
                    disabled={!canEdit}
                    onChange={(e) =>
                      safeSetConfig((c) => ({
                        ...c,
                        masters: {
                          ...c.masters,
                          priorities: c.masters.priorities.map((x) =>
                            x.id === p.id ? { ...x, enabled: e.target.checked } : x
                          ),
                        },
                      }))
                    }
                  />
                  On
                </label>
              </div>
              {[
                ["firstResponseMinutes", "First response (min)"],
                ["resolutionMinutes", "Resolution (min)"],
                ["autoUpgradeAfterMinutes", "Auto-upgrade (min)"],
              ].map(([field, label]) => (
                <div key={field}>
                  <DsLabel>{label}</DsLabel>
                  <DsInput
                    type="number"
                    disabled={!canEdit}
                    value={p[field] ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      safeSetConfig((c) => ({
                        ...c,
                        masters: {
                          ...c.masters,
                          priorities: c.masters.priorities.map((x) =>
                            x.id === p.id
                              ? {
                                  ...x,
                                  [field]: v === "" ? null : Number(v) || 0,
                                }
                              : x
                          ),
                        },
                      }));
                    }}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </FormModal>

<Panel
        title="Status lifecycle"
        description="Company-wide statuses available on tickets."
      >
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {statuses.map((s) => (
            <label
              key={s.id}
              className="flex items-center justify-between rounded-lg border border-[hsl(var(--ds-border))] px-3 py-2"
            >
              <span className="ds-body">
                {s.name}
                <span className="ml-2 ds-caption text-[hsl(var(--ds-muted-fg))]">
                  {s.id}
                  {s.pauseSla ? " · SLA pause" : ""}
                </span>
              </span>
              <input
                type="checkbox"
                checked={s.enabled !== false}
                disabled={!canEdit}
                onChange={(e) =>
                  safeSetConfig((cfg) => ({
                    ...cfg,
                    masters: {
                      ...cfg.masters,
                      statuses: cfg.masters.statuses.map((x) =>
                        x.id === s.id ? { ...x, enabled: e.target.checked } : x
                      ),
                    },
                  }))
                }
              />
            </label>
          ))}
        </div>
      </Panel>

      {/* Type form */}
      <FormModal
        open={!!typeForm}
        title={typeForm?.mode === "edit" ? "Edit ticket type" : "Add ticket type"}
        onClose={() => setTypeForm(null)}
        footer={
          <>
            <DsButton type="button" variant="outline" onClick={() => setTypeForm(null)}>
              Cancel
            </DsButton>
            <DsButton type="button" onClick={saveTypeForm}>
              Save
            </DsButton>
          </>
        }
      >
        {typeForm ? (
          <div className="space-y-3">
            <div>
              <DsLabel>Name</DsLabel>
              <DsInput
                value={typeForm.name}
                onChange={(e) => setTypeForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. HR"
              />
            </div>
            <label className="inline-flex items-center gap-2 ds-body">
              <input
                type="checkbox"
                checked={!!typeForm.enabled}
                onChange={(e) =>
                  setTypeForm((f) => ({ ...f, enabled: e.target.checked }))
                }
              />
              Enabled
            </label>
          </div>
        ) : null}
      </FormModal>

      {/* Category form */}
      <FormModal
        open={!!categoryForm}
        title={categoryForm?.mode === "edit" ? "Edit category" : "Add category"}
        onClose={() => setCategoryForm(null)}
        footer={
          <>
            <DsButton type="button" variant="outline" onClick={() => setCategoryForm(null)}>
              Cancel
            </DsButton>
            <DsButton type="button" onClick={saveCategoryForm}>
              Save
            </DsButton>
          </>
        }
      >
        {categoryForm ? (
          <div className="space-y-3">
            <div>
              <DsLabel>Map to type</DsLabel>
              <select
                className={fieldClass}
                value={categoryForm.typeId || ""}
                onChange={(e) =>
                  setCategoryForm((f) => ({ ...f, typeId: e.target.value }))
                }
              >
                <option value="">Select type…</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <DsLabel>Name</DsLabel>
              <DsInput
                value={categoryForm.name}
                onChange={(e) =>
                  setCategoryForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="e.g. Leave query"
              />
            </div>
            <label className="inline-flex items-center gap-2 ds-body">
              <input
                type="checkbox"
                checked={!!categoryForm.enabled}
                onChange={(e) =>
                  setCategoryForm((f) => ({ ...f, enabled: e.target.checked }))
                }
              />
              Enabled
            </label>
          </div>
        ) : null}
      </FormModal>

      {/* Sub form */}
      {/* Category full configure */}
      <FormModal
        open={!!categoryConfig}
        title={categoryConfig ? `Configure — ${categoryConfig.label}` : "Configure"}
        onClose={() => setCategoryConfig(null)}
        wide
        footer={
          <DsButton type="button" onClick={() => setCategoryConfig(null)}>
            Done
          </DsButton>
        }
      >
        {categoryConfig ? (
          <div className="space-y-4">
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
              All ticket rules for this category. Most specific level wins. Roles are
              loaded from User Roles.
            </p>
            <div className="flex flex-wrap gap-1 border-b border-[hsl(var(--ds-border))] pb-2">
              {[
                { id: "priorities", label: "Priorities" },
                { id: "sla", label: "SLA pause" },
                { id: "escalation", label: "Escalation" },
                { id: "workflow", label: "Workflow" },
                { id: "rca", label: "RCA" },
                { id: "assignment", label: "Assignment" },
                { id: "advanced", label: "Advanced" },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`rounded-md px-3 py-1.5 text-sm ${
                    configTab === t.id
                      ? "bg-[hsl(var(--ds-primary))] font-semibold text-[hsl(var(--ds-primary-fg))]"
                      : "text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                  onClick={() => setConfigTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {configTab === "priorities" ? (
              <div className="space-y-3">
                <div className="flex justify-end">
                  <DsButton
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!canEditLevelPolicies || !activeCategoryPolicy?.priorities}
                    onClick={() =>
                      clearLevelSection("category", categoryConfig.ids, "priorities")
                    }
                  >
                    Inherit company defaults
                  </DsButton>
                </div>
                {categoryPriorityRows.map((row, idx) => (
                  <div
                    key={row.id}
                    className="grid grid-cols-1 gap-2 rounded-lg border border-[hsl(var(--ds-border))] p-3 md:grid-cols-4"
                  >
                    <div className="font-semibold">{row.name || row.id}</div>
                    {["firstResponseMinutes", "resolutionMinutes", "autoUpgradeAfterMinutes"].map(
                      (field) => (
                        <div key={field}>
                          <DsLabel>
                            {field === "firstResponseMinutes"
                              ? "First response (min)"
                              : field === "resolutionMinutes"
                                ? "Resolution (min)"
                                : "Auto-upgrade (min)"}
                          </DsLabel>
                          <DsInput
                            type="number"
                            disabled={!canEditLevelPolicies}
                            value={row[field] ?? ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              const next = categoryPriorityRows.map((r, i) =>
                                i === idx
                                  ? {
                                      ...r,
                                      [field]: v === "" ? null : Number(v) || 0,
                                    }
                                  : r
                              );
                              patchLevelPolicy(
                                "category",
                                categoryConfig.ids,
                                "priorities",
                                next.map(
                                  ({
                                    id,
                                    firstResponseMinutes,
                                    resolutionMinutes,
                                    autoUpgradeAfterMinutes,
                                    enabled,
                                  }) => ({
                                    id,
                                    enabled,
                                    firstResponseMinutes,
                                    resolutionMinutes,
                                    autoUpgradeAfterMinutes,
                                  })
                                )
                              );
                            }}
                          />
                        </div>
                      )
                    )}
                  </div>
                ))}
              </div>
            ) : null}

            {configTab === "sla" ? (
              <div className="space-y-2">
                {[
                  ["onHoldStatus", "On hold status"],
                  ["waitingOnCustomer", "Waiting on customer"],
                  ["pendingApproval", "Pending approval"],
                  ["vendorDependency", "Vendor dependency"],
                  ["weeklyOff", "Weekly off"],
                  ["companyHoliday", "Company holiday"],
                ].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 ds-body">
                    <input
                      type="checkbox"
                      disabled={!canEditLevelPolicies}
                      checked={!!(activeCategoryPolicy?.sla?.pause || config.sla?.pause || {})[k]}
                      onChange={(e) =>
                        updateCategorySla({
                          pause: {
                            ...(activeCategoryPolicy?.sla?.pause || config.sla?.pause || {}),
                            [k]: e.target.checked,
                          },
                        })
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>
            ) : null}

            {configTab === "escalation" ? (
              <div className="space-y-3">
                <div className="flex flex-wrap justify-end gap-2">
                  <DsButton
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={
                      !canEditLevelPolicies || !activeCategoryPolicy?.sla?.escalationMatrix
                    }
                    onClick={() => {
                      const next = { ...(activeCategoryPolicy?.sla || {}) };
                      delete next.escalationMatrix;
                      patchLevelPolicy("category", categoryConfig.ids, "sla", next);
                    }}
                  >
                    Clear override
                  </DsButton>
                  <DsButton
                    type="button"
                    size="sm"
                    disabled={!canEditLevelPolicies}
                    onClick={() => {
                      const current = Array.isArray(activeCategoryPolicy?.sla?.escalationMatrix)
                        ? activeCategoryPolicy.sla.escalationMatrix
                        : (config.sla?.escalationMatrix || []).map((r) => ({
                            ...r,
                            notifyRoles: [...(r.notifyRoles || [])],
                          }));
                      updateCategorySla({
                        escalationMatrix: [
                          ...current,
                          {
                            level: (current.at(-1)?.level || 0) + 1,
                            breach: "resolution",
                            notifyRoles: [],
                          },
                        ],
                      });
                    }}
                  >
                    <DsIcon icon={Plus} size="sm" /> Add level
                  </DsButton>
                </div>
                {categoryEscalation.map((row, idx) => {
                  const isOverride = Array.isArray(activeCategoryPolicy?.sla?.escalationMatrix);
                  const matrix = isOverride
                    ? activeCategoryPolicy.sla.escalationMatrix
                    : categoryEscalation;
                  const ensureOverride = () =>
                    isOverride
                      ? matrix
                      : matrix.map((r) => ({
                          ...r,
                          notifyRoles: [...(r.notifyRoles || [])],
                        }));
                  return (
                    <div
                      key={`${row.level}-${idx}`}
                      className="space-y-3 rounded-xl border border-[hsl(var(--ds-border))] p-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="ds-h4">
                          Level {row.level}
                          {!isOverride ? (
                            <span className="ml-2 ds-caption font-normal text-[hsl(var(--ds-muted-fg))]">
                              (defaults — edit to override)
                            </span>
                          ) : null}
                        </div>
                        <DsButton
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={!canEditLevelPolicies}
                          onClick={() => {
                            const base = ensureOverride();
                            updateCategorySla({
                              escalationMatrix: base.filter((_, i) => i !== idx),
                            });
                          }}
                        >
                          <DsIcon icon={Trash2} size="sm" />
                        </DsButton>
                      </div>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div>
                          <DsLabel>Breach type</DsLabel>
                          <select
                            disabled={!canEditLevelPolicies}
                            value={row.breach || "resolution"}
                            className="h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm"
                            onChange={(e) => {
                              const breach = e.target.value;
                              const base = ensureOverride();
                              updateCategorySla({
                                escalationMatrix: base.map((r, i) =>
                                  i === idx ? { ...r, breach } : r
                                ),
                              });
                            }}
                          >
                            <option value="first_response">First response</option>
                            <option value="resolution">Resolution</option>
                          </select>
                        </div>
                        <div>
                          <DsLabel>Level number</DsLabel>
                          <DsInput
                            type="number"
                            disabled={!canEditLevelPolicies}
                            value={row.level ?? ""}
                            onChange={(e) => {
                              const level = Number(e.target.value) || 1;
                              const base = ensureOverride();
                              updateCategorySla({
                                escalationMatrix: base.map((r, i) =>
                                  i === idx ? { ...r, level } : r
                                ),
                              });
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <DsLabel>Notify roles (from User Roles)</DsLabel>
                        <RoleMultiSelect
                          options={policyRoleOptions}
                          disabled={!canEditLevelPolicies}
                          value={row.notifyRoles || []}
                          onChange={(notifyRoles) => {
                            const base = ensureOverride();
                            updateCategorySla({
                              escalationMatrix: base.map((r, i) =>
                                i === idx ? { ...r, notifyRoles } : r
                              ),
                            });
                          }}
                        />
                        {(row.notifyRoles || []).length > 0 ? (
                          <p className="mt-1 ds-caption text-[hsl(var(--ds-muted-fg))]">
                            Selected:{" "}
                            {(row.notifyRoles || [])
                              .map((c) => getRoleLabel(policyRoleOptions, c))
                              .join(", ")}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {configTab === "workflow" ? (
              <div className="space-y-2">
                <label className="flex items-center gap-2 ds-body">
                  <input
                    type="checkbox"
                    disabled={!canEditLevelPolicies}
                    checked={
                      activeCategoryPolicy?.workflow?.makerCheckerEnabled ??
                      !!config.workflow?.makerCheckerEnabled
                    }
                    onChange={(e) =>
                      patchLevelPolicy("category", categoryConfig.ids, "workflow", {
                        ...(activeCategoryPolicy?.workflow || {}),
                        makerCheckerEnabled: e.target.checked,
                      })
                    }
                  />
                  Maker-checker enabled
                </label>
                <label className="flex items-center gap-2 ds-body">
                  <input
                    type="checkbox"
                    disabled={!canEditLevelPolicies}
                    checked={
                      activeCategoryPolicy?.workflow?.approvalBasedTicketsEnabled ??
                      !!config.workflow?.approvalBasedTicketsEnabled
                    }
                    onChange={(e) =>
                      patchLevelPolicy("category", categoryConfig.ids, "workflow", {
                        ...(activeCategoryPolicy?.workflow || {}),
                        approvalBasedTicketsEnabled: e.target.checked,
                      })
                    }
                  />
                  Approval-based tickets
                </label>
              </div>
            ) : null}

            {configTab === "rca" ? (
              <div className="space-y-2">
                <label className="flex items-center gap-2 ds-body">
                  <input
                    type="checkbox"
                    disabled={!canEditLevelPolicies}
                    checked={
                      activeCategoryPolicy?.rca?.mandatory ??
                      !!config.rca?.mandatoryForClosure
                    }
                    onChange={(e) =>
                      patchLevelPolicy("category", categoryConfig.ids, "rca", {
                        ...(activeCategoryPolicy?.rca || {}),
                        mandatory: e.target.checked,
                      })
                    }
                  />
                  RCA mandatory on close
                </label>
                <label className="flex items-center gap-2 ds-body">
                  <input
                    type="checkbox"
                    disabled={!canEditLevelPolicies}
                    checked={
                      activeCategoryPolicy?.rca?.resolutionNotesRequired ??
                      !!config.rca?.closureCompliance?.requireResolutionNotes
                    }
                    onChange={(e) =>
                      patchLevelPolicy("category", categoryConfig.ids, "rca", {
                        ...(activeCategoryPolicy?.rca || {}),
                        resolutionNotesRequired: e.target.checked,
                      })
                    }
                  />
                  Resolution notes required
                </label>
                <label className="flex items-center gap-2 ds-body">
                  <input
                    type="checkbox"
                    disabled={!canEditLevelPolicies}
                    checked={
                      activeCategoryPolicy?.rca?.feedbackEnabled ??
                      !!config.feedback?.enabled
                    }
                    onChange={(e) =>
                      patchLevelPolicy("category", categoryConfig.ids, "rca", {
                        ...(activeCategoryPolicy?.rca || {}),
                        feedbackEnabled: e.target.checked,
                      })
                    }
                  />
                  Collect requester feedback after close
                </label>
              </div>
            ) : null}

            {configTab === "assignment" ? (
              <div className="space-y-3">
                <div>
                  <DsLabel>Route new tickets to role</DsLabel>
                  <select
                    disabled={!canEditLevelPolicies}
                    className="h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm"
                    value={
                      activeCategoryPolicy?.assignment?.routeTo?.key ||
                      ""
                    }
                    onChange={(e) => {
                      const key = e.target.value;
                      patchLevelPolicy("category", categoryConfig.ids, "assignment", {
                        ...(activeCategoryPolicy?.assignment || {}),
                        autoAssignmentEnabled: !!key,
                        routeTo: key ? { kind: "role", key } : null,
                      });
                    }}
                  >
                    <option value="">— Manual queue / no auto-route —</option>
                    {policyRoleOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                        {opt.isSystem ? " (system)" : ""}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 ds-caption text-[hsl(var(--ds-muted-fg))]">
                    Roles are created in User Roles. Assignees with this role see tickets in
                    Approvals.
                  </p>
                </div>
                <label className="flex items-center gap-2 ds-body">
                  <input
                    type="checkbox"
                    disabled={!canEditLevelPolicies}
                    checked={
                      activeCategoryPolicy?.assignment?.allowManualReassign ??
                      !!config.assignment?.allowManualReassign
                    }
                    onChange={(e) =>
                      patchLevelPolicy("category", categoryConfig.ids, "assignment", {
                        ...(activeCategoryPolicy?.assignment || {}),
                        allowManualReassign: e.target.checked,
                      })
                    }
                  />
                  Allow manual reassign
                </label>
              </div>
            ) : null}

            {configTab === "advanced" ? (
              <div className="space-y-3">
                <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                  Advanced options for this category only.
                </p>
                {[
                  ["allowMerge", "Allow merge tickets"],
                  ["allowSplit", "Allow split tickets"],
                  ["knowledgeBaseEnabled", "Suggest knowledge-base articles"],
                  ["prioritizationRulesEnabled", "Apply auto-priority rules"],
                  ["auditTrailEnabled", "Immutable audit trail"],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 ds-body">
                    <input
                      type="checkbox"
                      disabled={!canEditLevelPolicies}
                      checked={
                        activeCategoryPolicy?.advanced?.[key] ??
                        !!config.advanced?.[key]
                      }
                      onChange={(e) =>
                        patchLevelPolicy("category", categoryConfig.ids, "advanced", {
                          ...(activeCategoryPolicy?.advanced || {}),
                          [key]: e.target.checked,
                        })
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </FormModal>
    </div>
  );
}
